import { FC, useEffect, useState, useCallback, useMemo, memo } from "react";
import BtnFilter from "../../buttons/btnFilter.component/BtnFilter";
import Filter from "./Filter";
import { SelectedOptions } from "../../../models/FilterModels/IFilterModel";
import CancelButton from "../../buttons/CancelButton/CancelButton";
import "./Filter.css";
import { IFilterOptions } from "../../../common/api_interfaces/filter/IFilterOptions";

interface FilterContainerProps {
    filterCounter: number;
    setFilterCounter: (counter: number) => void;
    selectedOptions: SelectedOptions;
    setSelectedOptions: (options: SelectedOptions) => void;
    filterOptions: IFilterOptions | undefined;
    showResetButton?: boolean;
    onReset?: () => void;
    showApplyButton?: boolean;
    onApply?: () => void;
}

const FilterContainer: FC<FilterContainerProps> = memo(({
    filterCounter,
    setFilterCounter,
    selectedOptions,
    setSelectedOptions,
    filterOptions,
    showResetButton = false,
    onReset,
    showApplyButton = false,
    onApply,
}) => {
    const [openFilter, setOpenFilter] = useState<boolean>(false);

    const calculateFilterCounter = useCallback((options: SelectedOptions): number => {
        let counter = 0;
        if (options.dates) counter += 1;
        if (options.responsibleIds?.length) counter += options.responsibleIds.length;
        if (options.statusIds?.length) counter += options.statusIds.length;
        if (options.typeTaskIds?.length) counter += options.typeTaskIds.length;
        if (options.priorityIds?.length) counter += options.priorityIds.length;
        if (options.roleIds?.length) counter += options.roleIds.length;
        if (options.fileTypes?.length) counter += options.fileTypes.length;
        if (options.entityTypes?.length) counter += options.entityTypes.length;
        if (options.actionTypes?.length) counter += options.actionTypes.length;
        if (options.projectIds?.length) counter += options.projectIds.length;
        if (options.historyAuthorIds?.length) counter += options.historyAuthorIds.length;
        return counter;
    }, []);

    useEffect(() => {
        const counter = calculateFilterCounter(selectedOptions);
        setFilterCounter(counter);
    }, [selectedOptions, setFilterCounter, calculateFilterCounter]);

    const toggleFilter = useCallback(() => {
        setOpenFilter(prev => !prev);
    }, []);

    const closeFilter = useCallback(() => {
        setOpenFilter(false);
    }, []);

    const handleApply = useCallback(async () => {
        closeFilter();
        if (onApply) {
            await onApply();
        }
    }, [closeFilter, onApply]);

    const handleReset = useCallback(() => {
        if (onReset) {
            onReset();
        }
    }, [onReset]);

    const shouldShowResetButton = useMemo(() => 
        filterCounter !== 0 || showResetButton, 
    [filterCounter, showResetButton]);

    const overlayStyle = useMemo(() => ({
        width: "100vw",
        height: "100vh",
        position: "fixed" as const,
        top: "0",
        left: "0",
    }), []);

    return (
        <div style={{ display: "flex", position: "relative" }}>
            <BtnFilter counter={filterCounter} onClick={toggleFilter} />
            
            {openFilter && (
                <div>
                    <div className="filter-container" onClick={(e) => e.stopPropagation()}>
                        <Filter
                            options={filterOptions}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            showApplyButton={showApplyButton}
                            onApply={handleApply}
                        />
                    </div>
                    <div
                        style={overlayStyle}
                        onClick={closeFilter}
                    />
                </div>
            )}
            
            {shouldShowResetButton && (
                <CancelButton onClickFunc={handleReset} />
            )}
        </div>
    );
});

FilterContainer.displayName = 'FilterContainer';

export default FilterContainer;